import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  CambioFormulario,
  CampoFormulario,
  ConfiguracionFormulario,
  DisplayFormulario,
  OpcionCampo,
  OrientacionFormulario,
  PosicionLabelFormulario,
  TipoCampoFormulario,
  ValorFormulario,
} from './utilidades/formulario.models';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  timer,
} from 'rxjs';
import { AutorizacionService } from 'src/app/compartido/autorizacion/autorizacion.service';
import { CENTRO_BOGOTA, CONFIGURACION_MAPA_FORMULARIO } from './utilidades/formulario.static';
import { GoogleMap } from '@angular/google-maps';
import {
  formatearFecha,
  fechaToMoment,
  valorNumérico,
  normalizarString,
} from '../utilidades/valores.utils';
import * as moment from 'moment';
import { DialogosService } from 'src/app/compartido/servicios/dialogos.service';
import { FileInput } from 'ngx-custom-material-file-input';
import { ErrorDesarrollo } from '../utilidades/error.utils';

@Component({
  selector: 'compartido-formulario',
  templateUrl: './formulario.component.html',
  styleUrls: ['./formulario.component.scss'],
  standalone: false,
})
export class FormularioComponent implements OnChanges, AfterViewInit, OnDestroy {
  /* Suscripción */
  private _suscripcion: Subscription = new Subscription();
  private _suscripcionValor: Subscription = new Subscription();

  /* Configuración del formulario */
  @Input({ required: true }) configuracion!: ConfiguracionFormulario | null;

  /* Formulario */
  protected formulario = new FormGroup({
    campos: new FormArray<FormControl>([]),
  });
  get campos() {
    return this.formulario.controls.campos as FormArray;
  }
  get cambiado() {
    return this.formulario.dirty;
  }

  /* Opciones de los campos */
  protected opcionesCampo: Record<string, OpcionCampo<string | number>[]> = {};

  protected onBlurMiles(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/\./g, '').trim();
    const num = parseInt(raw, 10);
    if (!isNaN(num) && raw !== '') input.value = num.toLocaleString('de-DE');
  }

  protected onFocusMiles(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\./g, '');
  }

  /* Buscadores de los campos */
  protected buscadorCampo: Record<string, FormControl> = {};

  /* Constantes */
  readonly TipoCampoFormulario = TipoCampoFormulario;
  readonly OrientacionFormulario = OrientacionFormulario;
  readonly PosicionLabelFormulario = PosicionLabelFormulario;
  readonly CONFIGURACION_MAPA_FORMULARIO = CONFIGURACION_MAPA_FORMULARIO;
  readonly CENTRO_MAPA = CENTRO_BOGOTA;

  /* Subject para emitir valores */
  private readonly _emitirValores$ = new Subject<ValorFormulario>();

  /* Output de valor */
  @Output() valor: EventEmitter<ValorFormulario> = new EventEmitter<ValorFormulario>();

  /* Output de validez */
  @Output() valido: EventEmitter<boolean> = new EventEmitter<boolean>();

  /* Output de valor y validez */
  @Output() cambios: EventEmitter<CambioFormulario> = new EventEmitter<CambioFormulario>();

  /* Referencias para Mapas */
  @ViewChildren('mapa') private _mapas!: GoogleMap[];

  constructor(
    public readonly cdr: ChangeDetectorRef,
    protected readonly autorizacionService: AutorizacionService,
    private readonly _dialogosService: DialogosService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const cfg = this.configuracion;

    // Asegurarse de que la configuración no cambie a null
    if (cfg === null) {
      if (changes['configuracion'].isFirstChange()) return;
      throw new ErrorDesarrollo('La configuración no puede cambiar de un valor a null');
    }

    // Escuchar cambios en el formulario
    this._suscripcionValor.unsubscribe();
    this._suscripcionValor = this.formulario.valueChanges.subscribe(() =>
      this._emitirValorFormulario(),
    );

    // Escuchar subject para emitir valores
    let prev: any = {};
    const escucharDatos = this._emitirValores$
      .pipe(
        debounceTime(cfg.debounce ?? 150),
        distinctUntilChanged(),
        switchMap((value) => {
          // Calcular el debounce máximo entre los campos que cambiaron
          const debounces = this.configuracion!.campos.map((c) => c.debounce ?? 0);
          const valor = value['campos'];
          const cambios = Object.keys(valor).filter((key) => prev[key] !== valor[key]);
          prev = valor;
          if (cambios.length === 0) return of(value);
          const maxDebounce = Math.max(
            0,
            ...cambios.map((campo) => debounces[valorNumérico(campo)!] ?? 0),
          );
          return timer(maxDebounce).pipe(map(() => value));
        }),
      )
      .subscribe(() => this._emitirValorFormulario(false));
    this._suscripcionValor.add(escucharDatos);

    // Limpiar opciones y buscadores de los campos
    this.opcionesCampo = {};
    this.buscadorCampo = {};

    // Crear los campos del formulario
    this.campos.clear();
    for (const campo of cfg.campos) this.campos.push(this._crearFormControl(campo));

    // Asignar valores iniciales a los campos
    // Se hace aquí para que todos los campos estén inicializados y puedan reaccionar a los valores
    for (const [index, campo] of cfg.campos.entries()) {
      if (campo.valor !== undefined && campo.valor !== null) {
        const control = this.campos.at(index);
        if (campo.tipo === TipoCampoFormulario.FECHA) {
          campo.valor ? control.setValue(fechaToMoment(campo.valor)) : control.setValue(null);
        } else {
          control.setValue(campo.valor);
        }
      }
    }

    // Tomar los valores iniciales como referencia, para que la primera emisión
    // no se demore el debounce de los campos con valor por defecto
    prev = this.formulario.getRawValue()['campos'];

    // Emitir los valores iniciales del formulario
    this._emitirValorFormulario();
  }

  ngAfterViewInit(): void {
    // Emitir los valores iniciales del formulario
    if (this.configuracion !== null) this._emitirValorFormulario();
  }

  ngOnDestroy() {
    this._suscripcion.unsubscribe();
    this._suscripcionValor.unsubscribe();
  }

  /**
   * Crea un validador de cantidad máxima de dígitos para un campo numérico.
   * `Validators.maxLength` solo mide valores con `length` (texto y arreglos), así que en
   * un campo numérico no validaría nada.
   * @param {number} longitudMaxima - La cantidad máxima de dígitos permitida.
   * @returns {ValidatorFn} - El validador del control.
   * @author Juan Corral
   */
  private _validadorDigitosMaximos(longitudMaxima: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const valor = control.value;
      if (valor === null || valor === undefined || valor === '') return null;
      // Se cuentan solo los dígitos, para no contar separadores de miles ni el signo
      const digitos = String(valor).replace(/\D/g, '').length;
      if (digitos <= longitudMaxima) return null;
      return { maxlength: { requiredLength: longitudMaxima, actualLength: digitos } };
    };
  }

  /**
   * Crear una suscripción que escucha y emite los cambios en el formulario.
   * @param {boolean} debounce [Opcional] - Si se debe usar debounce.
   * @author Juan Corral
   */
  private _emitirValorFormulario(debounce: boolean = true) {
    if (debounce) {
      this._emitirValores$.next(this.formulario.getRawValue());
      return;
    }

    const valor = this._formatearValores(this.formulario.getRawValue());
    const valido = this.formulario.valid;
    this.valido.next(valido);
    this.valor.next(valor);
    this.cambios.emit({ valor: valor, valido: valido });
  }

  /**
   * Formatea los valores del formulario.
   * @param {any} valores - Los valores del formulario.
   * @returns {ValorFormulario} - Los valores del formulario calculados.
   * @author Juan Corral
   */
  private _formatearValores(valores: any): ValorFormulario {
    const valoresFormulario: ValorFormulario = {};
    const campos = this.configuracion?.campos ?? [];
    for (const [index, valor] of valores.campos?.entries() ?? []) {
      const campo = campos[index];
      if (moment.isMoment(valor)) {
        valoresFormulario[campo.slug] = formatearFecha(valor);
      } else if (campo.tipo === TipoCampoFormulario.NUMERO) {
        const rawValor =
          campo.separadorMiles && typeof valor === 'string' ? valor.replace(/\./g, '') : valor;
        const numero = valorNumérico(rawValor, campo.decimal ? 'float' : 'int');
        valoresFormulario[campo.slug] = rawValor ? numero : null;
      } else {
        valoresFormulario[campo.slug] = valor;
      }
    }
    return valoresFormulario;
  }

  /**
   * Limpia los campos del formulario.
   * @author Juan Corral
   */
  public limpiarCampos(): void {
    this.campos.reset();
  }

  /**
   * Devuelve un FormControl con la configuración del campo.
   * @param {CampoFormulario} campo - Configuración del campo.
   * @returns {FormControl} - FormControl con la configuración del campo.
   * @author Juan Corral
   */
  private _crearFormControl(campo: CampoFormulario): FormControl {
    // Crear el control
    let control: FormControl;
    switch (campo.tipo) {
      case TipoCampoFormulario.TEXTO:
        control = new FormControl<string | undefined>(undefined);
        if (campo.correo === true) control.addValidators(Validators.email);
        if (campo.longitudMaxima !== undefined)
          control.addValidators(Validators.maxLength(campo.longitudMaxima));
        break;
      case TipoCampoFormulario.NUMERO:
        control = new FormControl<number | undefined>(undefined);
        if (campo.longitudMaxima !== undefined)
          control.addValidators(this._validadorDigitosMaximos(campo.longitudMaxima));
        if (campo.minimo !== undefined) control.addValidators(Validators.min(campo.minimo));
        if (campo.maximo !== undefined) control.addValidators(Validators.max(campo.maximo));
        break;
      case TipoCampoFormulario.FECHA:
        control = new FormControl<string | undefined>(undefined);
        break;
      case TipoCampoFormulario.ARCHIVO:
        control = new FormControl<File | undefined>(undefined);
        break;
      case TipoCampoFormulario.OPCION_MULTIPLE:
        control = campo.multiple
          ? new FormControl<(string | number)[]>([])
          : new FormControl<string | number | undefined>(undefined);
        break;
      case TipoCampoFormulario.AUTOCOMPLETE:
        control = new FormControl<string | number | undefined>(undefined);
        break;
      case TipoCampoFormulario.BOOLEANO:
        control = new FormControl<boolean | undefined>(undefined);
        break;
      case TipoCampoFormulario.COORDENADAS:
        control = new FormControl<{ latitud: string; longitud: string } | undefined>(undefined);
        break;
      default:
        control = new FormControl(undefined);
    }

    // Deshabilitar el control, si es necesario
    if (campo.disabled === true) control.disable();

    // Asignar validadores al control, si existen
    if (campo.requerido === true) control.addValidators(Validators.required);
    if (campo.validadores !== undefined)
      control.addValidators(campo.validadores.map((validador) => validador.validador));
    control.updateValueAndValidity();

    // Emitir los valores del control, si es necesario
    if (campo.valorOut) {
      this._suscripcion.add(
        control.valueChanges.subscribe((valor) => {
          campo.valorOut!.next(valor as never);
        }),
      );
    }

    // Suscribirse a los cambios de valor del control, si es necesario
    if (campo.valorIn) {
      if (campo.tipo === TipoCampoFormulario.FECHA) {
        this._suscripcion.add(
          campo.valorIn.subscribe((valor) =>
            valor ? control.setValue(fechaToMoment(valor)) : control.setValue(null),
          ),
        );
      } else if (campo.tipo === TipoCampoFormulario.COORDENADAS) {
        this._suscripcion.add(
          campo.valorIn.subscribe((valor) => {
            this.ponerMarcadorMapa(campo, {
              lat: valor.latitud,
              lng: valor.longitud,
            });
          }),
        );
      } else this._suscripcion.add(campo.valorIn.subscribe((valor) => control.setValue(valor)));
    }

    // Suscribirse a los cambios de requerido del control, si es necesario
    if (campo.requeridoIn) {
      this._suscripcion.add(
        campo.requeridoIn.subscribe((requerido) => {
          campo.requerido = requerido;
          if (requerido === true) control.setValidators(Validators.required);
          else control.removeValidators(Validators.required);
          control.updateValueAndValidity();
        }),
      );
    }

    // Agregar opciones al control, si es necesario
    if (
      campo.tipo === TipoCampoFormulario.OPCION_MULTIPLE ||
      campo.tipo === TipoCampoFormulario.AUTOCOMPLETE
    ) {
      this.opcionesCampo[campo.slug] = campo.opciones;
      if (campo.opcionesIn)
        this._suscripcion.add(
          campo.opcionesIn.subscribe((opciones) => {
            campo.opciones = opciones;
            this.opcionesCampo[campo.slug] = opciones;
            this.cdr.markForCheck();
          }),
        );
    }

    // Limitar el valor del control a las opciones, si es necesario
    if (campo.tipo === TipoCampoFormulario.AUTOCOMPLETE && campo.soloOpcion) {
      control.addValidators((cont: AbstractControl) => {
        const valor = cont.value;
        if (valor === null || valor === undefined) return null;
        if (!campo.opciones.some((opcion) => opcion.valor === valor)) return { soloOpcion: true };
        return null;
      });
    }

    // Agregar buscador al control, si es necesario
    if (campo.tipo === TipoCampoFormulario.OPCION_MULTIPLE && campo.buscador) {
      this.buscadorCampo[campo.slug] = new FormControl<string>('');
      // Emitir los valores del buscador, si es necesario
      if (campo.buscadorOut) {
        this._suscripcion.add(
          this.buscadorCampo[campo.slug].valueChanges
            .pipe(debounceTime(100), distinctUntilChanged())
            .subscribe((valor) => campo.buscadorOut!.next(valor)),
        );
      }
    }

    // Filtrar las opciones del control, si es necesario
    if (
      (campo.tipo === TipoCampoFormulario.AUTOCOMPLETE && campo.filtrarOpciones) ||
      (campo.tipo === TipoCampoFormulario.OPCION_MULTIPLE && campo.buscador)
    ) {
      const filtro$ =
        campo.tipo === TipoCampoFormulario.AUTOCOMPLETE
          ? control.valueChanges
          : this.buscadorCampo[campo.slug].valueChanges;
      const escucharValor = filtro$
        .pipe(
          debounceTime(100),
          distinctUntilChanged(),
          map((valor) => {
            if (valor === null || valor === undefined) return campo.opciones;
            const valorNormalizado = normalizarString(valor);
            return campo.opciones.filter((opcion) => {
              const nombreNormalizado = normalizarString(opcion.nombre);
              return nombreNormalizado.includes(valorNormalizado);
            });
          }),
        )
        .subscribe((opcionesFiltradas) => {
          this.opcionesCampo[campo.slug] = opcionesFiltradas;
          this.cdr.markForCheck();
        });
      this._suscripcion.add(escucharValor);
    }

    // Ocultar el campo, si es requerido por el rol
    if (campo.roles)
      this.autorizacionService.tieneRol$(campo.roles).then((tieneRol) => {
        campo.oculto = !tieneRol;
        this.cdr.markForCheck();
      });

    return control;
  }

  /**
   * Devuelve si el label debe ser mostrado en la posición proveída.
   * @param {PosicionLabelFormulario} posicion - La posición a verificar.
   * @returns {boolean} - Si el label debe ser mostrado en la posición proveída.
   * @author Juan Corral
   */
  protected mostrarLabel(posicion: PosicionLabelFormulario): boolean {
    if (
      this.configuracion!.estilo?.posicionLabel === undefined &&
      posicion === PosicionLabelFormulario.DENTRO
    )
      return true;
    return this.configuracion!.estilo?.posicionLabel === posicion;
  }

  /**
   * Devuelve las clases para el tipo elemento proveído.
   * @param {string} posicion - El tipo de elemento.
   * @returns {string[]} - Las clases para el tipo elemento proveído.
   * @author Juan Corral
   */
  protected obtenerClases(elemento: string): string[] {
    const clases: string[] = [];

    if (elemento === 'campos') {
      const orientacion = this.configuracion!.estilo?.orientacion;
      switch (orientacion) {
        case OrientacionFormulario.VERTICAL:
          clases.push('vertical');
          break;
        case OrientacionFormulario.HORIZONTAL:
          clases.push('horizontal');
          break;
        default:
          clases.push('horizontal');
      }
      const display = this.configuracion!.estilo?.display;
      switch (display) {
        case DisplayFormulario.FLEX:
          clases.push('flex');
          break;
        case DisplayFormulario.GRID:
          clases.push('grid');
          break;
        default:
          clases.push('flex');
      }
    } else if (elemento === 'campo') {
      const posicionLabel = this.configuracion!.estilo?.posicionLabel;
      switch (posicionLabel) {
        case PosicionLabelFormulario.TOP:
          clases.push('label-top');
          break;
        case PosicionLabelFormulario.IZQUIERDA:
          clases.push('label-izquierda');
          break;
      }
    }

    return clases;
  }

  /**
   * Devuelve el mensaje de error del control proveído.
   * @param {FormControl} control - El control a extraer el error.
   * @returns {string} - El mensaje de error del control.
   * @author Juan Corral
   */
  protected obtenerErrorControl(control: AbstractControl): string {
    if (control.hasError('required')) {
      return 'Campo requerido';
    } else if (control.hasError('min')) {
      return 'Valor mínimo: ' + control.getError('min').min;
    } else if (control.hasError('max')) {
      return 'Valor máximo: ' + control.getError('max').max;
    } else if (control.hasError('minlength')) {
      return `Mínimo de caracteres: ${control.getError('minlength').requiredLength}`;
    } else if (control.hasError('maxlength')) {
      return `Máximo de caracteres: ${control.getError('maxlength').requiredLength}`;
    } else if (control.hasError('email')) {
      return 'Correo inválido';
    } else if (control.hasError('pattern')) {
      return 'Formato inválido';
    } else if (control.hasError('matDatepickerParse')) {
      return 'Fecha inválida';
    } else if (control.hasError('soloOpcion')) {
      return 'No es una opción de la lista';
    } else if (control.errors && Object.keys(control.errors).length > 0) {
      return Object.entries(control.errors)[0][0];
    } else {
      return '';
    }
  }

  /**
   * Pone el marcador en el mapa del campo proveído.
   * @param {CampoFormulario} campo - El campo con las coordenadas.
   * @param {google.maps.LatLngLiteral} coordenadas - Las coordenadas del marcador.
   * @author Juan Corral
   */
  protected ponerMarcadorMapa(
    campo: CampoFormulario,
    coordenadas: google.maps.LatLngLiteral,
  ): void {
    if (campo.readonly === true) return;
    const idxCampo = this.configuracion!.campos.findIndex((c) => c.slug === campo.slug);
    if (idxCampo === -1) return;
    const control = this.campos.at(idxCampo) as FormControl;
    const mapa = this._mapas.find((m) => m.mapId === campo.slug);
    if (!mapa) return;
    const valor = coordenadas ? { latitud: coordenadas.lat, longitud: coordenadas.lng } : null;
    control.setValue(valor);
    mapa.panTo(coordenadas);
    this.formulario.updateValueAndValidity();
    this.formulario.markAsDirty();
    this.formulario.markAsTouched();
    this.cdr.markForCheck();
  }

  /**
   * Abre la URL proveída en una nueva pestaña.
   * @param {string} url - La URL a abrir.
   * @author Juan Corral
   */
  protected abrirUrl(url: string): void {
    window.open(url, '_blank');
  }

  /**
   * Escanea un documento y lo guarda en el control proveído.
   * @param {CampoFormulario} campo - El campo del formulario.
   * @param {AbstractControl} control - El control donde se guardará el documento.
   * @param {MouseEvent} event - El evento que disparó la acción.
   * @returns {Promise<void>} - Promesa que se resuelve cuando se escanea el documento y se guarda en el campo proveído.
   */
  protected async escanearDocumento$(
    campo: CampoFormulario,
    control: AbstractControl,
    event: MouseEvent,
  ): Promise<void> {
    if (campo.tipo !== TipoCampoFormulario.ARCHIVO) return;
    event.stopPropagation();
    event.preventDefault();

    /**
     * El escáner se carga bajo demanda: es standalone y arrastra
     * `ng2-pdf-viewer`, que solo funciona en el navegador. Al no importarlo de
     * forma estática queda en su propio chunk y `FormularioModule` deja de
     * depender de él.
     */
    const { EscanerComponent } = await import('./utilidades/escaner/escaner.component');

    const archivo: File | undefined = await this._dialogosService.abrirVistaPrevia$({
      componente: EscanerComponent,
      inputs: {
        habilitarCamara: campo.escaner!.includes('camara'),
        habilitarCarga: campo.escaner!.includes('carga'),
        habilitarImagenes: campo.escaner!.includes('imagenes'),
      },
      output: 'pdf',
    });
    if (!archivo) return;
    control.setValue(new FileInput([archivo]));
  }
}
